import { getSportsnewBetErrorCode } from './sportsnewBetErrorMessage.js';
import { parseSportsnewOddStateId } from '../../../_context/_helpers/sportsnewOddStateId.js';

export const SPORTSNEW_SELECTED_RECOVERY_CODES = new Set([
  'ODDS_CHANGED',
  'ODDS_STALE',
  'MARKET_UNAVAILABLE',
  'SELECTION_UNAVAILABLE',
  'SELECTION_MODE_MISMATCH',
]);

const firstDefined = (...values) => values.find(
  value => value !== undefined && value !== null && value !== '',
);

export const sportsnewPreviewFailurePayload = (source) => {
  if (!source || typeof source !== 'object') return {};
  if (source.response?.data && typeof source.response.data === 'object') {
    return source.response.data;
  }
  return source;
};

export const sportsnewPreviewFailureDetails = (payload = {}) => {
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : {};
  return nested.details || payload.details || {};
};

const previewFailureRefresh = (
  payload = {},
  details = sportsnewPreviewFailureDetails(payload),
) => {
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : {};
  return details.refresh || nested.refresh || payload.refresh || {};
};

export const sportsnewPreviewLatestOdds = (
  payload = {},
  details = sportsnewPreviewFailureDetails(payload),
) => {
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const refresh = previewFailureRefresh(payload, details);
  const latestOdds = firstDefined(
    refresh.latestOdds,
    refresh.latest_odds,
    details.latestOdds,
    details.latest_odds,
    nested.latestOdds,
    nested.latest_odds,
    payload.latestOdds,
    payload.latest_odds,
  );
  return Array.isArray(latestOdds) ? latestOdds : [];
};

export const stableSportsnewLatestOddsRevision = latestOdds => (
  Array.isArray(latestOdds)
    ? latestOdds
      .map((odd = {}) => ({
        oddStateId: firstDefined(odd.oddStateId, odd.odd_state_id, odd.odds_id, odd.id),
        priceDecimal: firstDefined(odd.priceDecimal, odd.price_decimal, odd.odds, odd.price),
        state: firstDefined(odd.state, odd.displayState, odd.display_state),
        bettable: firstDefined(odd.bettable, odd.isBettable, odd.is_bettable),
        oddBettable: firstDefined(odd.oddBettable, odd.odd_bettable),
        marketBettable: firstDefined(odd.marketBettable, odd.market_bettable),
        marketSuspended: firstDefined(odd.marketSuspended, odd.market_suspended),
        providerOddId: firstDefined(odd.providerOddId, odd.provider_odd_id),
        oddIdentityKey: firstDefined(odd.oddIdentityKey, odd.odd_identity_key),
        shortOddId: firstDefined(odd.shortOddId, odd.short_odd_id),
        canonicalSelectionKey: firstDefined(
          odd.canonicalSelectionKey,
          odd.canonical_selection_key,
        ),
        canonicalLineKey: firstDefined(odd.canonicalLineKey, odd.canonical_line_key),
        canonicalPairKey: firstDefined(odd.canonicalPairKey, odd.canonical_pair_key),
        selectionLine: firstDefined(odd.selectionLine, odd.selection_line),
        groupingKey: firstDefined(odd.groupingKey, odd.grouping_key),
        points: odd.points,
        line: odd.line,
        sourceReceivedAt: firstDefined(odd.sourceReceivedAt, odd.source_received_at),
        updatedAt: firstDefined(odd.updatedAt, odd.updated_at),
      }))
      .sort((left, right) => String(left.oddStateId ?? '').localeCompare(String(right.oddStateId ?? '')))
    : []
);

const finiteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizedState = value => String(value ?? '').trim().toUpperCase();

const authoritativeLifecyclePatch = (odd = {}) => {
  const state = normalizedState(firstDefined(odd.state, odd.displayState, odd.display_state));
  const aggregateBettable = firstDefined(odd.bettable, odd.isBettable, odd.is_bettable);
  const oddBettable = firstDefined(odd.oddBettable, odd.odd_bettable);
  const marketBettable = firstDefined(odd.marketBettable, odd.market_bettable);
  const suspended = firstDefined(
    odd.marketSuspended,
    odd.market_suspended,
    odd.suspended,
    odd.is_suspended,
  );
  const unavailable = odd.unavailable === true || state === 'UNAVAILABLE';
  const explicitlyClosed = unavailable
    || aggregateBettable === false
    || oddBettable === false
    || marketBettable === false
    || suspended === true
    || ['LOCKED', 'SUSPENDED'].includes(state);
  if (explicitlyClosed) {
    return {
      bettable: false,
      locked: !unavailable,
      is_suspended: suspended === true || state === 'SUSPENDED',
      ...(unavailable ? { unavailable: true } : {}),
    };
  }

  // Closing may be inferred from any explicit negative authority. Reopening
  // is deliberately stricter: a price by itself can never reopen a pick.
  const explicitlyOpen = ['OPEN', 'AVAILABLE'].includes(state)
    || (
      oddBettable === true
      && marketBettable === true
      && suspended === false
    )
    || (aggregateBettable === true && suspended === false);
  return explicitlyOpen
    ? { bettable: true, locked: false, is_suspended: false, unavailable: false }
    : {};
};

const identityField = (value, camel, snake) => firstDefined(value?.[camel], value?.[snake]);
const ownsIdentityField = (value, camel, snake) => (
  Object.prototype.hasOwnProperty.call(value ?? {}, camel)
  || Object.prototype.hasOwnProperty.call(value ?? {}, snake)
);

const selectionIdentityChanged = (selection = {}, latest = {}) => {
  const stringFields = [
    ['providerOddId', 'provider_odd_id'],
    ['oddIdentityKey', 'odd_identity_key'],
    ['shortOddId', 'short_odd_id'],
    ['canonicalSelectionKey', 'canonical_selection_key'],
    ['canonicalLineKey', 'canonical_line_key'],
    ['canonicalPairKey', 'canonical_pair_key'],
    ['selectionLine', 'selection_line'],
    ['groupingKey', 'grouping_key'],
  ];
  for (const [camel, snake] of stringFields) {
    const accepted = identityField(selection, camel, snake);
    if (accepted == null || !ownsIdentityField(latest, camel, snake)) continue;
    const current = identityField(latest, camel, snake);
    if (String(accepted) !== String(current ?? '')) return true;
  }
  for (const [camel, snake] of [['points', 'points'], ['line', 'line']]) {
    const accepted = identityField(selection, camel, snake);
    if (accepted == null || !ownsIdentityField(latest, camel, snake)) continue;
    const current = identityField(latest, camel, snake);
    if (current == null || Number(accepted) !== Number(current)) return true;
  }
  return false;
};

/**
 * Normalize authoritative, exact-id requote rows embedded in a rejected API
 * response. Identity replacement is never implicit: a changed contract fails
 * the existing pick closed so acceptance cannot silently retarget a bet.
 */
export const extractSportsnewAuthoritativeRequoteUpdates = (
  source,
  submittedSelections = [],
) => {
  const code = getSportsnewBetErrorCode(source);
  if (!SPORTSNEW_SELECTED_RECOVERY_CODES.has(code)) return [];

  const submittedById = new Map((Array.isArray(submittedSelections) ? submittedSelections : [])
    .map(selection => [
      parseSportsnewOddStateId(firstDefined(
        selection?.oddStateId,
        selection?.odd_state_id,
        selection?.odds_id,
      )),
      selection,
    ])
    .filter(([oddStateId]) => oddStateId != null));
  if (submittedById.size === 0) return [];

  const payload = sportsnewPreviewFailurePayload(source);
  const details = sportsnewPreviewFailureDetails(payload);
  const rows = [...sportsnewPreviewLatestOdds(payload, details)];
  const detailOddStateId = parseSportsnewOddStateId(firstDefined(
    details.oddStateId,
    details.odd_state_id,
  ));
  if (detailOddStateId != null && !rows.some(row => parseSportsnewOddStateId(firstDefined(
    row?.oddStateId,
    row?.odd_state_id,
    row?.odds_id,
    row?.id,
  )) === detailOddStateId)) {
    rows.push(details);
  }

  const updatesById = new Map();
  rows.forEach((odd = {}) => {
    const oddStateId = parseSportsnewOddStateId(firstDefined(
      odd.oddStateId,
      odd.odd_state_id,
      odd.odds_id,
      odd.id,
    ));
    const submitted = submittedById.get(oddStateId);
    if (!submitted || updatesById.has(oddStateId)) return;

    const identityChanged = selectionIdentityChanged(submitted, odd);
    const terminalReason = identityChanged
      ? 'SELECTION_IDENTITY_CHANGED'
      : (code === 'SELECTION_MODE_MISMATCH' ? 'SELECTION_MODE_MISMATCH' : null);
    let lifecyclePatch = terminalReason
      ? {
        unavailable: true,
        bettable: false,
        locked: true,
        reason: terminalReason,
      }
      : authoritativeLifecyclePatch(odd);
    const priceDecimal = terminalReason ? null : finiteNumberOrNull(firstDefined(
      odd.priceDecimal,
      odd.price_decimal,
      odd.currentPriceDecimal,
      odd.current_price_decimal,
      odd.odds,
      odd.price,
    ));
    // A selection cannot be safely reopened without a current numeric price.
    // Negative lifecycle authority remains valid without a price, but a
    // positive state with no price is incomplete and must fall back to REST.
    if (priceDecimal == null && lifecyclePatch.bettable === true) {
      lifecyclePatch = {};
    }
    if (priceDecimal == null && Object.keys(lifecyclePatch).length === 0) return;

    const acceptedPrice = finiteNumberOrNull(firstDefined(
      submitted.odds,
      submitted.expectedPriceDecimal,
      submitted.expected_price_decimal,
    ));
    const lifecycleNeedsAcceptance = lifecyclePatch.unavailable === true
      || lifecyclePatch.bettable === false
      || lifecyclePatch.locked === true;
    updatesById.set(oddStateId, {
      oddStateId,
      priceDecimal,
      requiresAcceptance: terminalReason != null
        || lifecycleNeedsAcceptance
        || (
          priceDecimal != null
          && acceptedPrice != null
          && Math.abs(priceDecimal - acceptedPrice) > 0.001
        ),
      sourceReceivedAt: firstDefined(odd.sourceReceivedAt, odd.source_received_at),
      updatedAt: firstDefined(odd.updatedAt, odd.updated_at),
      authority: 'preview_rejection',
      patch: lifecyclePatch,
    });
  });

  return [...updatesById.values()].sort((left, right) => left.oddStateId - right.oddStateId);
};

export default Object.freeze({
  extractSportsnewAuthoritativeRequoteUpdates,
  sportsnewPreviewFailureDetails,
  sportsnewPreviewFailurePayload,
  sportsnewPreviewLatestOdds,
  stableSportsnewLatestOddsRevision,
});
